import React from 'react'
import { useLocation, useNavigate, matchPath } from 'react-router-dom'
import {
	useAppDispatch,
	useAppSelector,
	authActions,
	ConfigStateType,
	CrudType,
	UserMenuAccessType,
	MenuType,
} from '@redux/index'
import config, { SidePageType } from '@configs'

interface PropsType {
	children: JSX.Element
}

export const AccessControlGuard = (props: PropsType) => {
	// hook
	const location = useLocation()
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const userAccessMenuList: UserMenuAccessType[] = useAppSelector(
		state => state.auth.userAccessMenuList,
	)
	const adminMenu: MenuType[] = useAppSelector(state => state.admin.menu)

	// state
	const [isAuthValidate, setIsAuthValidate] = React.useState<boolean>(true)

	// variables
	const sidePageList: SidePageType[] = config.sidePage()
	const isAdmin = React.useMemo(() => {
		const idx = adminMenu.findIndex(aMenu => aMenu.to === location.pathname)

		return idx > 0
	}, [location.pathname])

	React.useEffect(() => {
		const crud: CrudType = getAccessControlCRUD()

		if (crud.includes('R')) {
			setIsAuthValidate(true)
		} else {
			setIsAuthValidate(false)
		}

		reduxDispatch(authActions.CURRENT_ACCESS(crud))
	}, [isAuthValidate, location.pathname])

	React.useEffect(() => {
		if (!isAuthValidate) {
			if (mode === 'main') {
				navigate('/main/home')
			} else {
				navigate('/admin/home')
			}
		}
	}, [isAuthValidate, location.pathname])

	const getAccessControlCRUD = React.useCallback((): CrudType => {
		const defaultPageUris: string[] = ['/main/home', '/admin/home']
		if (defaultPageUris.includes(location.pathname)) {
			return ['R']
		}

		const thisMenuRes = getThisMenuIdAndType()

		if (thisMenuRes.type === 'side') {
			const thisSidePageArr = sidePageList.filter(
				aSidePage => aSidePage.id === thisMenuRes.id,
			)

			const isValidSidePage: boolean =
				thisSidePageArr.length > 0 && thisSidePageArr[0].parentPath !== ''

			if (isValidSidePage) {
				const parentMenuArr = userAccessMenuList.filter(
					aUserAccessMenu => aUserAccessMenu.uri === thisSidePageArr[0].parentPath,
				)

				const res2: CrudType =
					parentMenuArr.length > 0 ? (parentMenuArr[0].crud.split('') as CrudType) : []

				return res2
			}

			return []
		} else {
			const index = userAccessMenuList.findIndex(aUserAccessMenu => {
				return aUserAccessMenu.menuId === thisMenuRes.id
			})

			const res: CrudType =
				index === -1 ? [] : (userAccessMenuList[index].crud.split('') as CrudType)

			return res
		}
	}, [mode, userAccessMenuList, location.pathname])

	const getThisMenuIdAndType = React.useCallback((): {
		id: string
		type?: 'admin' | 'main' | 'side'
	} => {
		if (isAdmin) {
			const thisAdminMenu = adminMenu.filter(anAdminMenu => {
				const aSidePageTo: string = anAdminMenu.to ?? ''
				const matchRes = matchPath(aSidePageTo, location.pathname)
				const isMatched = matchRes !== null

				return isMatched
			})

			if (thisAdminMenu.length > 0) {
				return { id: thisAdminMenu[0].id, type: 'admin' }
			} else {
				return { id: '' }
			}
		} else {
			const thisSidePage = sidePageList.filter(aSidePage => {
				const aSidePageTo: string = aSidePage.to ?? ''
				const matchRes = matchPath(aSidePageTo, location.pathname)
				const isMatched = matchRes !== null

				return isMatched
			})
			const thisMenuPage = userAccessMenuList.filter(aMainPage => {
				const aSidePageTo: string = aMainPage.uri ?? ''
				const matchRes = matchPath(aSidePageTo, location.pathname)
				const isMatched = matchRes !== null

				return isMatched
			})

			if (thisMenuPage.length > 0) {
				return { id: thisMenuPage[0].menuId, type: 'main' }
			} else if (thisSidePage.length > 0) {
				return { id: thisSidePage[0].id, type: 'side' }
			} else {
				return { id: '' }
			}
		}
	}, [isAdmin, location.pathname, adminMenu])

	return <>{props.children}</>
}
